import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, Video, Image as ImageIcon, X, Plus } from "lucide-react";
import { toast } from "sonner";

export default function AvailableMeCompose() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    author_name: "", author_handle: "guest", author_title: "", author_company: "",
    type: "video_essay", headline: "", body: "", tags: "",
    videos: [{ url: "", title: "" }],
    images: [{ url: "", alt: "" }],
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map(t => t.trim().toLowerCase()).filter(Boolean),
        videos: form.videos.filter(v => v.url),
        images: form.images.filter(i => i.url),
      };
      const { data } = await api.post("/social/post", payload);
      toast.success("Posted to AvailableMe.");
      navigate(`/availableme`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post.");
    }
  };

  const addVideo = () => setForm({ ...form, videos: [...form.videos, { url: "", title: "" }] });
  const removeVideo = (i) => setForm({ ...form, videos: form.videos.filter((_, x) => x !== i) });
  const addImage = () => setForm({ ...form, images: [...form.images, { url: "", alt: "" }] });
  const removeImage = (i) => setForm({ ...form, images: form.images.filter((_, x) => x !== i) });

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="compose-page">
      <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Compose</div>
      <h1 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter">Publish an interactive page.</h1>
      <p className="text-gray-700 mt-2">Up to 4 videos. Up to 4 images. Make it big.</p>

      <form onSubmit={submit} className="mt-10 space-y-5 border border-gray-300 p-7" data-testid="compose-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <input className="input-federal" required placeholder="Your name" value={form.author_name} onChange={e => setForm({...form, author_name: e.target.value})} data-testid="cp-name" />
          <input className="input-federal" placeholder="Handle (no spaces)" value={form.author_handle} onChange={e => setForm({...form, author_handle: e.target.value.replace(/\s/g, "-").toLowerCase()})} data-testid="cp-handle" />
          <input className="input-federal" placeholder="Title (e.g. Managing Partner)" value={form.author_title} onChange={e => setForm({...form, author_title: e.target.value})} data-testid="cp-title" />
          <input className="input-federal" placeholder="Company / Firm" value={form.author_company} onChange={e => setForm({...form, author_company: e.target.value})} data-testid="cp-company" />
        </div>

        <select className="input-federal" value={form.type} onChange={e => setForm({...form, type: e.target.value})} data-testid="cp-type">
          <option value="mass_opportunity">Mass Opportunity (deal call-out)</option>
          <option value="video_essay">Video Essay</option>
          <option value="announcement">Announcement</option>
        </select>

        <input className="input-federal" required placeholder="Headline (the big one)" value={form.headline} onChange={e => setForm({...form, headline: e.target.value})} data-testid="cp-headline" />
        <textarea className="input-federal" rows={6} required placeholder="Body — tell the story" value={form.body} onChange={e => setForm({...form, body: e.target.value})} data-testid="cp-body" />
        <input className="input-federal" placeholder="Tags (comma-separated)" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} data-testid="cp-tags" />

        <div className="border-t border-gray-200 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <Video className="w-4 h-4 text-[#D97706]" />
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B]">Videos · {form.videos.length}/4 · paste YouTube embed URLs</div>
          </div>
          {form.videos.map((v, i) => (
            <div key={`v-${i}`} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
              <input className="input-federal md:col-span-3" placeholder="https://www.youtube.com/embed/…" value={v.url} onChange={e => {
                const next = [...form.videos]; next[i] = { ...next[i], url: e.target.value }; setForm({...form, videos: next});
              }} data-testid={`cp-video-url-${i}`} />
              <input className="input-federal md:col-span-2" placeholder="Title (optional)" value={v.title} onChange={e => {
                const next = [...form.videos]; next[i] = { ...next[i], title: e.target.value }; setForm({...form, videos: next});
              }} data-testid={`cp-video-title-${i}`} />
              {form.videos.length > 1 && (
                <button type="button" onClick={() => removeVideo(i)} className="text-[#D97706] mono text-[11px] inline-flex items-center gap-1 md:col-span-5 -mt-1">
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
          ))}
          {form.videos.length < 4 && (
            <button type="button" onClick={addVideo} className="btn-ghost mt-2" data-testid="cp-add-video"><Plus className="w-3 h-3" /> Add Video</button>
          )}
        </div>

        <div className="border-t border-gray-200 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon className="w-4 h-4 text-[#D97706]" />
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B]">Images · {form.images.length}/4</div>
          </div>
          {form.images.map((img, i) => (
            <div key={`i-${i}`} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
              <input className="input-federal md:col-span-3" placeholder="Image URL" value={img.url} onChange={e => {
                const next = [...form.images]; next[i] = { ...next[i], url: e.target.value }; setForm({...form, images: next});
              }} data-testid={`cp-image-url-${i}`} />
              <input className="input-federal md:col-span-2" placeholder="Alt (optional)" value={img.alt} onChange={e => {
                const next = [...form.images]; next[i] = { ...next[i], alt: e.target.value }; setForm({...form, images: next});
              }} data-testid={`cp-image-alt-${i}`} />
              {form.images.length > 1 && (
                <button type="button" onClick={() => removeImage(i)} className="text-[#D97706] mono text-[11px] inline-flex items-center gap-1 md:col-span-5 -mt-1">
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
          ))}
          {form.images.length < 4 && (
            <button type="button" onClick={addImage} className="btn-ghost mt-2" data-testid="cp-add-image"><Plus className="w-3 h-3" /> Add Image</button>
          )}
        </div>

        <button type="submit" className="btn-signal w-full justify-center" data-testid="cp-submit">Publish <ArrowRight className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
